import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { buildPath } from './lib/tenant';
import {
    Container, Header, Loader, Dimmer, Message, Card, Icon, Label, Button, Confirm,
} from 'semantic-ui-react';
import moment from 'moment';
import { getMyOrders, cancelOrder } from './lib/tickets';
import { track } from './lib/telemetry';
import { t } from './locales';

function addToCalendar(order) {
    const start = moment(order.event_date).format('YYYYMMDDTHHmmss');
    const end = moment(order.event_date).add(2, 'hours').format('YYYYMMDDTHHmmss');
    const ics = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//Selwyn Ticketing//EN',
        'BEGIN:VEVENT',
        `SUMMARY:${order.event_name}`,
        `DTSTART:${start}`,
        `DTEND:${end}`,
        `LOCATION:${order.event_location || ''}`,
        `DESCRIPTION:Order ref: ${order.order_ref}`,
        'END:VEVENT',
        'END:VCALENDAR',
    ].join('\r\n');
    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${order.event_name.replace(/[^a-z0-9]/gi, '_')}.ics`;
    a.click();
    URL.revokeObjectURL(url);
}

const STATUS_BADGE = {
    paid:      { label: () => t('myTickets.statusConfirmed'), color: 'var(--colour_heading_success)' },
    pending:   { label: () => t('myTickets.statusPending'),   color: '#f0ad4e' },
    cancelled: { label: () => t('myTickets.statusCancelled'), color: '#bbb' },
    refunded:  { label: () => t('myTickets.statusRefunded'),  color: '#bbb' },
};

function MyTickets() {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [confirmRef, setConfirmRef] = useState(null);
    const [cancelError, setCancelError] = useState(null);

    const { data, isLoading, isError } = useQuery({
        queryKey: ['my-orders'],
        queryFn: getMyOrders,
    });

    const cancelMutation = useMutation({
        mutationFn: cancelOrder,
        onSuccess: (result) => {
            if (result.error) {
                setCancelError(result.error);
            } else {
                track('order_cancelled', '/my-tickets', { order_ref: result.order_ref });
                queryClient.invalidateQueries(['my-orders']);
            }
            setConfirmRef(null);
        },
        onError: () => {
            setCancelError(t('myTickets.cancelFailed'));
            setConfirmRef(null);
        },
    });

    if (isLoading) {
        return (
            <Dimmer active inverted page>
                <Loader size="large" content={t('myTickets.loading')} />
            </Dimmer>
        );
    }

    if (isError) {
        return (
            <Container className="contentContainer">
                <Message negative>
                    <Message.Header>{t('myTickets.loadErrorTitle')}</Message.Header>
                    <p>{t('myTickets.loadErrorBody')}</p>
                </Message>
            </Container>
        );
    }

    const orders = data?.orders ?? [];
    const upcoming = orders.filter(o => o.event_date && new Date(o.event_date) >= new Date());
    const past = orders.filter(o => !o.event_date || new Date(o.event_date) < new Date());

    const renderOrders = (list, allowCancel = false) => (
        <Card.Group stackable itemsPerRow={2}>
            {list.map(order => {
                const badge = STATUS_BADGE[order.status] ?? STATUS_BADGE.pending;
                const isCancelledOrRefunded = order.status === 'cancelled' || order.status === 'refunded';

                return (
                    <Card
                        key={order.id}
                        className="ticketCard"
                        style={{ borderRadius: 12, opacity: isCancelledOrRefunded ? 0.7 : 1 }}
                    >
                        <Card.Content
                            as={Link}
                            to={buildPath(`/my-tickets/${order.order_ref}`)}
                            style={{ textDecoration: 'none' }}
                        >
                            <Card.Header style={{ color: isCancelledOrRefunded ? '#999' : 'var(--colour_menu_main)', marginBottom: '0.5rem' }}>
                                {order.event_name}
                            </Card.Header>

                            <div style={{ marginBottom: '0.5rem' }}>
                                <span style={{
                                    display: 'inline-block',
                                    backgroundColor: badge.color,
                                    color: '#fff',
                                    borderRadius: 5,
                                    padding: '2px 8px',
                                    fontSize: '0.78rem',
                                    fontWeight: 700,
                                }}>
                                    {badge.label()}
                                </span>
                            </div>

                            {order.event_date && (
                                <Card.Meta>
                                    <Icon name="calendar alternate outline" />
                                    {moment(order.event_date).format('ddd D MMM YYYY, HH:mm')}
                                </Card.Meta>
                            )}
                            {order.event_location && (
                                <Card.Meta>
                                    <Icon name="map marker alternate" />
                                    {order.event_location}
                                </Card.Meta>
                            )}

                            {order.status === 'refunded' && (
                                <Card.Meta style={{ marginTop: '0.4rem', color: '#888' }}>
                                    <Icon name="undo" />
                                    {t('myTickets.refundProcessed')}
                                </Card.Meta>
                            )}
                            {order.status === 'cancelled' && (
                                <Card.Meta style={{ marginTop: '0.4rem', color: '#888' }}>
                                    <Icon name="times circle outline" />
                                    {t('myTickets.bookingCancelled')}
                                </Card.Meta>
                            )}
                        </Card.Content>

                        <Card.Content extra style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.4rem' }}>
                            <Label style={{ backgroundColor: isCancelledOrRefunded ? '#ddd' : 'var(--colour_heading_success)', color: isCancelledOrRefunded ? '#888' : '#fff' }}>
                                <Icon name="ticket" />
                                {order.quantity > 1
                                    ? t('myTickets.ticketCountPlural', { quantity: order.quantity })
                                    : t('myTickets.ticketCountSingular', { quantity: order.quantity })}
                            </Label>

                            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                                {allowCancel && order.event_date && !isCancelledOrRefunded && (
                                    <Button
                                        size="mini"
                                        basic
                                        icon="calendar plus"
                                        title={t('myTickets.addToCalendarTitle')}
                                        onClick={e => { e.preventDefault(); addToCalendar(order); }}
                                    />
                                )}
                                {allowCancel && !isCancelledOrRefunded && (
                                    <Button
                                        size="mini"
                                        basic
                                        negative
                                        onClick={e => { e.preventDefault(); setConfirmRef(order.order_ref); }}
                                    >
                                        {t('myTickets.cancelButton')}
                                    </Button>
                                )}
                            </div>
                        </Card.Content>
                    </Card>
                );
            })}
        </Card.Group>
    );

    return (
        <Container className="contentContainer">
            <Header as="h1" className="sectionHeader" style={{ marginBottom: '2rem' }}>
                {t('myTickets.title')}
            </Header>

            {cancelError && (
                <Message negative onDismiss={() => setCancelError(null)}>
                    {cancelError}
                </Message>
            )}

            {orders.length === 0 && (
                <div style={{ textAlign: 'center', padding: '4rem 2rem' }}>
                    <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🎟</div>
                    <Header as="h3" style={{ color: 'var(--colour_menu_main)' }}>
                        {t('myTickets.emptyTitle')}
                    </Header>
                    <p style={{ color: '#777', marginBottom: '1.5rem' }}>
                        {t('myTickets.emptySubtitle')}
                    </p>
                    <Button
                        size="large"
                        onClick={() => navigate(buildPath('/events'))}
                        style={{ backgroundColor: 'var(--colour_menu_main)', color: '#fff' }}
                    >
                        {t('myTickets.browseEvents')}
                    </Button>
                </div>
            )}

            {upcoming.length > 0 && (
                <>
                    <Header as="h3" style={{ color: 'var(--colour_grey)', marginBottom: '1rem' }}>
                        {t('myTickets.upcoming')}
                    </Header>
                    {renderOrders(upcoming, true)}
                </>
            )}

            {past.length > 0 && (
                <>
                    <Header as="h3" style={{ color: '#aaa', marginTop: '2.5rem', marginBottom: '1rem' }}>
                        {t('myTickets.pastEvents')}
                    </Header>
                    {renderOrders(past, false)}
                </>
            )}

            <Confirm
                open={confirmRef !== null}
                header={t('myTickets.cancelBookingHeader')}
                content={t('myTickets.cancelBookingContent', { orderRef: confirmRef })}
                confirmButton={t('myTickets.confirmCancel')}
                cancelButton={t('myTickets.keepBooking')}
                onCancel={() => setConfirmRef(null)}
                onConfirm={() => cancelMutation.mutate(confirmRef)}
            />
        </Container>
    );
}

export default MyTickets;
